import type { CreditEntry, CreditKind } from '../types';

// The rules the server applies to a transfer are repeated here only so the
// page can say what is wrong before a request is made. The server decides.

export const MAX_TRANSFER = 1_000_000;
export const MAX_NOTE = 200;

/** What a reseller can send: its balance less the whole credits it owes for pay-per-use viewing. */
export function availableCredits(balance: number, usageOwed: number): number {
  return Math.max(0, balance - Math.floor(usageOwed));
}

// ── Who may open the credits page, and which one ─────────────────────────────

export const CREDITS_ROUTE_ROLES = ['admin', 'client', 'reseller'] as const;

export type CreditsPage = 'operator' | 'send';

/** An operator adjusts a balance; a reseller sends credits to its own sub-resellers; nobody else has the page. */
export function creditsPageFor(role: string | undefined): CreditsPage | null {
  if (role === 'admin' || role === 'client') return 'operator';
  if (role === 'reseller') return 'send';
  return null;
}

// ── The form ─────────────────────────────────────────────────────────────────

/** A problem with the form: the translation key under `transfer.` and its values. */
export interface FormIssue {
  field: 'amount' | 'note';
  key: string;
  params?: Record<string, number>;
}

export type Validation = { ok: true; amount: number; note: string } | { ok: false; issues: FormIssue[] };

/** Checks what was typed against what the page knows; the server checks again. */
export function validateTransfer(input: { amount: string; note: string }, available: number): Validation {
  const issues: FormIssue[] = [];
  const raw = input.amount.trim();
  let amount = 0;
  if (!/^\d+$/.test(raw)) {
    issues.push({ field: 'amount', key: 'err_amount_whole' });
  } else {
    amount = Number(raw);
    if (amount < 1) issues.push({ field: 'amount', key: 'err_amount_min' });
    else if (amount > MAX_TRANSFER) issues.push({ field: 'amount', key: 'err_amount_max', params: { max: MAX_TRANSFER } });
    else if (amount > available) issues.push({ field: 'amount', key: 'err_amount_available', params: { available } });
  }
  const note = input.note.trim();
  if ([...note].length > MAX_NOTE) issues.push({ field: 'note', key: 'err_note_long', params: { max: MAX_NOTE } });
  if ([...note].some((c) => c.charCodeAt(0) < 0x20 || c.charCodeAt(0) === 0x7f)) issues.push({ field: 'note', key: 'err_note_control' });
  return issues.length ? { ok: false, issues } : { ok: true, amount, note };
}

// ── Failures ─────────────────────────────────────────────────────────────────

export type FailureKind =
  | 'insufficient' | 'recipient_ineligible' | 'not_found' | 'forbidden' | 'validation'
  | 'key_reused' | 'network' | 'server' | 'unknown';

export interface TransferFailure {
  kind: FailureKind;
  /** The sender's available credits, when the server says (insufficient). */
  available?: number;
  /**
   * The server may or may not have carried the request out: no answer, or an
   * error from the server itself. Sending it again with the same key is safe
   * and moves credits once. Every other failure is a refusal that moved nothing.
   */
  ambiguous: boolean;
}

/** Sorts an axios-shaped error into what the page should say and whether to retry the same request. */
export function classifyTransferError(e: unknown): TransferFailure {
  const err = e as { response?: { status?: number; data?: { available?: unknown } } } | null | undefined;
  const status = err?.response?.status;
  if (status === undefined) return { kind: 'network', ambiguous: true };
  switch (status) {
    case 402: {
      const a = err?.response?.data?.available;
      return { kind: 'insufficient', ambiguous: false, available: typeof a === 'number' ? a : undefined };
    }
    case 404: return { kind: 'not_found', ambiguous: false };
    case 409: return { kind: 'recipient_ineligible', ambiguous: false };
    case 401:
    case 403: return { kind: 'forbidden', ambiguous: false };
    case 400: return { kind: 'validation', ambiguous: false };
    case 422: return { kind: 'key_reused', ambiguous: false };
  }
  if (status >= 500) return { kind: 'server', ambiguous: true };
  return { kind: 'unknown', ambiguous: false };
}

// ── One attempt, one key ─────────────────────────────────────────────────────

export interface TransferRequest {
  toID: number;
  amount: number;
  note: string;
}

const fingerprint = (r: TransferRequest) => JSON.stringify([r.toID, r.amount, r.note]);

/**
 * Hands out the Idempotency-Key of a transfer. One attempt keeps one key for
 * as long as it may still be in flight: sending the same request again after
 * a lost answer or a server error uses the same key, so credits move once. A
 * changed request is a different attempt and gets a new key. An attempt ends,
 * and the next one starts with a new key, when the transfer succeeded or the
 * server refused it (a refusal moved nothing and recorded nothing).
 */
export class TransferAttempt {
  private current: { key: string; fingerprint: string } | null = null;
  private readonly newKey: () => string;

  constructor(newKey: () => string) {
    this.newKey = newKey;
  }

  keyFor(req: TransferRequest): string {
    const fp = fingerprint(req);
    if (!this.current || this.current.fingerprint !== fp) this.current = { key: this.newKey(), fingerprint: fp };
    return this.current.key;
  }

  succeeded(): void {
    this.current = null;
  }

  failed(f: TransferFailure): void {
    if (!f.ambiguous) this.current = null;
  }

  get open(): boolean {
    return this.current !== null;
  }
}

/** Lets one call run at a time: a second click while one is in flight does nothing. */
export class SingleFlight {
  private busy = false;

  async run<T>(fn: () => Promise<T>): Promise<T | undefined> {
    if (this.busy) return undefined;
    this.busy = true;
    try {
      return await fn();
    } finally {
      this.busy = false;
    }
  }
}

// ── History ──────────────────────────────────────────────────────────────────

export type TransferDirection = 'in' | 'out';

export interface TransferEntryView {
  direction: TransferDirection;
  amount: number;
  /** The other account's name, only when the ledger row records it; earlier transfers don't. */
  counterparty: string | null;
  note: string;
  /** Shared by both rows of a transfer made since migration 183. */
  reference: string | null;
}

const transferKinds: CreditKind[] = ['transfer_in', 'transfer_out'];

export function isTransfer(entry: Pick<CreditEntry, 'kind'>): boolean {
  return transferKinds.includes(entry.kind);
}

/** A transfer row as the history shows it, or null for any other kind of row. Nothing is guessed for older rows. */
export function transferEntryView(entry: CreditEntry): TransferEntryView | null {
  if (!isTransfer(entry)) return null;
  const name = entry.counterparty_id != null && entry.counterparty_name ? entry.counterparty_name : null;
  return {
    direction: entry.kind === 'transfer_in' ? 'in' : 'out',
    amount: Math.abs(entry.amount),
    counterparty: name,
    note: entry.note ?? '',
    reference: entry.transfer_id ?? null,
  };
}

/** The transfer rows of a ledger, in the order given. */
export function transferRows(history: CreditEntry[]): CreditEntry[] {
  return history.filter(isTransfer);
}

// ── What to tell the reseller ────────────────────────────────────────────────

const failureKeys: Record<FailureKind, string> = {
  insufficient: 'err_insufficient',
  recipient_ineligible: 'err_recipient_ineligible',
  not_found: 'err_not_found',
  forbidden: 'err_forbidden',
  validation: 'err_validation',
  key_reused: 'err_key_reused',
  network: 'err_network',
  server: 'err_server',
  unknown: 'err_unknown',
};

/**
 * The translation key (under `transfer.`) for a failure, with its values.
 * Only what this page knows is shown: the server's own wording never reaches
 * the screen, so an internal error can't leak through it.
 */
export function failureMessage(f: TransferFailure): { key: string; params?: Record<string, number> } {
  if (f.kind === 'insufficient' && f.available !== undefined) return { key: 'err_insufficient_known', params: { available: f.available } };
  return { key: failureKeys[f.kind] };
}

// ── After a transfer ─────────────────────────────────────────────────────────

/** The cached queries a transfer makes stale: both ledgers, the sub-resellers and their overview, and the dashboard's credit figures. */
export function refreshKeys(senderID: number, recipientID: number): (string | number)[][] {
  return [['my-credits', senderID], ['reseller-credits', recipientID], ['resellers'], ['dashboard-subresellers'], ['dashboard-runway'], ['dashboard-stats']];
}
